import { Injectable } from '@angular/core';
import { FA_SOLID_ICON_NAMES } from './fa-solid-icon-names';

@Injectable({
  providedIn: 'root'
})
export class MarkerIconService {
  private fontAvailable = true;

  constructor() {
    this.probeFont();
  }

  /**
   * Map kebab-case (location-dot) to the FA export-style name (locationDot),
   * then accept either form if it matches Font Awesome 6.7.2 Free solid.
   */
  lookupSolidIconName(markerIcon: any): string | null {
    const raw = this.normalizeIconName(markerIcon);
    if (!raw) {
      return null;
    }
    const kebab = raw.toLowerCase();
    if (FA_SOLID_ICON_NAMES[kebab]) {
      return kebab;
    }
    // Allow camelCase from an icon API (locationDot → location-dot)
    const fromCamel = raw.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
    if (FA_SOLID_ICON_NAMES[fromCamel]) {
      return fromCamel;
    }
    return null;
  }

  usesCustomMarkerIcon(tour: any, quest: any): boolean {
    return this.fontAvailable && !!this.resolveCustomIconName(tour, quest);
  }

  resolveCustomIconName(tour: any, quest: any): string | null {
    if (!tour?.isCustomMarkers) {
      return null;
    }
    try {
      return this.lookupSolidIconName(quest?.markerIcon);
    } catch {
      return null;
    }
  }

  /** Pin fill: quest.color only when custom markers are on. */
  getPinFill(tour: any, quest: any, fallback: string): string {
    if (tour?.isCustomMarkers && quest?.color) {
      return quest.color;
    }
    return fallback;
  }

  /** Question pin vs info pin. Custom icons never switch to lock/clock/finish. */
  isQuestionPinShape(quest: any): boolean {
    return !!quest?.isQuestion;
  }

  /** kebab-case → camelCase (location-dot → locationDot) */
  toIconApiName(kebab: string): string {
    return String(kebab || '').replace(/-([a-z0-9])/g, (_m, c) => String(c).toUpperCase());
  }

  private probeFont() {
    try {
      const doc = document as any;
      if (typeof document === 'undefined' || !doc.fonts || !doc.fonts.load) {
        return;
      }
      doc.fonts.load('900 16px "Font Awesome 6 Free"').then((loaded) => {
        if (!loaded || !loaded.length) {
          this.fontAvailable = false;
        }
      }).catch(() => {
        this.fontAvailable = false;
      });
    } catch {
      // Keep the optimistic default; tests and missing Font Loading API stay usable.
    }
  }

  private normalizeIconName(markerIcon: any): string {
    if (markerIcon == null) {
      return '';
    }
    return String(markerIcon)
      .trim()
      .replace(/^fa-solid\s+/i, '')
      .replace(/^fas\s+/i, '')
      .replace(/^fa-/i, '');
  }
}
